import ForecastSummaryValue from "./ForecastSummaryValue.js";
import Model from "../../Model.js";
import MonthPeriod from "../MonthPeriod.js";
import ScenarioManager from "../template/ScenarioManager.js";

/**
 * Represent a prevision of the accounting future of a company based
 * on input from an accountant and past accounting data. A forecast is
 * generated by a `ForecastTemplateInterpreter` from a
 * `ForecastTemplate`.
 *
 * A forecast contains 3 reports (P&L, balance sheet and cash flow)
 * that are taken care of by a `ReportManager`.
 *
 * @param {string} id - A GUID string
 * @param {string} name - A user-visible short string to identify this
 *   forecast
 * @param {Date} creationDate - Represent when the forecast was
 *   created
 * @param {Date} lastEditDate - Represent the last time the forecast
 *   was changed
 * @param {MonthPeriod[]} referencePeriods - The periods associated to
 *   the past columns containing actual data
 * @param {MonthPeriod} currentPeriod - The periods associated to the
 *   column between the past and future ones
 * @param {MonthPeriod[]} predictedPeriods - The periods associated to
 *   the future columns
 * @param {ForecastTemplate} forecastTemplate - The template from
 * which the forecast was interpreted
 * @param {ReportManager} reportManager - Groups the 3 reports of the
 * forecast
 * @param {ScenarioManager} [scenarioManager] - This object is
 *   responsible for managing the collections of scenarios
 * @param {ForecastSummaryValue[]} [summaryValues] - Sums up the
 *   forecast for the archive rendering
 * @param {string} [description] - Description of the overarching
 *   prerequisites of the templates on a high-level view
 */
export default class Forecast extends Model {
	constructor({
		id,
		name,
		creationDate,
		lastEditDate,
		referencePeriods = [],
		currentPeriod,
		predictedPeriods = [],
		forecastTemplate,
		forecastTemplateName,
		reportManager,
		scenarioManager = new ScenarioManager(),
		summaryValues,
		description,
	} = {}) {
		super(...arguments);

		this._id = id;
		this._name = name;
		this._creationDate = creationDate;
		this._lastEditDate = lastEditDate;

		this._forecastTemplate = forecastTemplate;
		this._forecastTemplateName = forecastTemplateName;
		this._reportManager = reportManager;
		this._referencePeriods = referencePeriods;
		this._currentPeriod = currentPeriod;
		this._predictedPeriods = predictedPeriods;
		this._scenarioManager = scenarioManager;
		this._summaryValues = summaryValues;
		this._description = description;
		this._isEstimate = false;
	}

	accept(visitor) {
		return visitor.visitForecast(this);
	}

	isEstimate() {
		return this._isEstimate;
	}

	setIsEstimate(isEstimate) {
		this._isEstimate = isEstimate;
	}

	getId() {
		return this._id;
	}

	getName() {
		return this._name;
	}

	getCreationDate() {
		return this._creationDate;
	}

	getLastEditDate() {
		return this._lastEditDate;
	}

	getReferencePeriods() {
		return this._referencePeriods;
	}

	getNumberOfReferencePeriods() {
		return this.getReferencePeriods().length;
	}

	getLastReferencePeriod() {
		return this.getReferencePeriods().at(-1);
	}

	getCurrentPeriod() {
		return this._currentPeriod;
	}

	getCurrentPeriodString() {
		return this._currentPeriod.asDate().toISOString();
	}

	getPredictedPeriods() {
		return this._predictedPeriods;
	}

	getAllPeriods() {
		return this._referencePeriods.concat(this._predictedPeriods);
	}

	getNumberOfPredictedPeriods() {
		return this.getPredictedPeriods().length;
	}

	isInPeriod({ startDate, endDate }) {
		const predictedPeriods = this.getPredictedPeriods();
		const numberOfPredictedPeriods = predictedPeriods.length;

		if (numberOfPredictedPeriods === 0) return false;

		const forecastFirstPeriodStart = predictedPeriods[0].asDate();
		const forecastLastPeriodStart =
			predictedPeriods[numberOfPredictedPeriods - 1].asDate();

		return (
			forecastLastPeriodStart >= startDate &&
			forecastFirstPeriodStart <= endDate
		);
	}

	findFirstAvailableFiscalYearPeriod({ fiscalYearPeriods }) {
		let forecastPeriodIndex = 0;

		const fiscalYearPeriodIndex = fiscalYearPeriods.findIndex(
			(fiscalYearPeriod) =>
				(forecastPeriodIndex = this._findFiscalPeriodIndex({
					fiscalYearPeriod,
				})) > -1,
		);

		if (forecastPeriodIndex < 0 || fiscalYearPeriodIndex < 0)
			return { isFound: false };

		return {
			isFound: true,
			fiscalYearPeriodIndex,
			forecastPeriodIndex,
		};
	}

	setReportManager(reportManager) {
		this._reportManager = reportManager;
	}

	getRowByStableId(stableRowId) {
		return this._reportManager.getRowByStableId(stableRowId);
	}

	getRowForAccountNumber(accountNumber) {
		return this._reportManager.getRowForAccountNumber(accountNumber);
	}

	getReports() {
		return this._reportManager.getReports();
	}

	getReportsAsList() {
		return this._reportManager.getReportsAsList();
	}

	getFirstReport() {
		return this.getReportsAsList()[0];
	}

	getReportFromId(reportId) {
		return this._reportManager.getReportFromId(reportId);
	}

	getForecastTemplateId() {
		return this._forecastTemplate.getId();
	}

	getForecastTemplateName() {
		return this._forecastTemplateName;
	}

	getIncomeStatement() {
		return this._reportManager.getIncomeStatement();
	}

	getBalanceSheet() {
		return this._reportManager.getBalanceSheet();
	}

	getCashFlow() {
		return this._reportManager.getCashFlow();
	}

	getConfiguration() {
		return this._forecastTemplate.getConfiguration();
	}

	getSummaryValueKeys() {
		return this.getReportsAsList().flatMap((report) =>
			report.getSummaryValueKeys(),
		);
	}

	getSummaryValues() {
		return this._summaryValues;
	}

	getDescription() {
		return this._description;
	}

	setDescription(text) {
		this._description = text;
	}

	makeComputationSeriesSpecifications({ builder }) {
		return this.getReportsAsList().flatMap((report) =>
			report.makeComputationSeriesSpecifications({ builder }),
		);
	}

	makeComputationSeriesSpecificationsForPublication({ builder }) {
		const annualTrendKeyFigureIdInUse = "IncomeBeforeFinancial";
		return this.makeComputationSeriesSpecifications({
			builder: builder.withKeyFiguresSeriesSpecificationAndOperationType({
				keyFigureId: annualTrendKeyFigureIdInUse,
			}),
		});
	}

	getBaselineScenario() {
		return this._scenarioManager.getBaselineScenario();
	}

	setDataFromSeries(series) {
		this.getReportsAsList().forEach((report) =>
			report.setDataFromSeries(series),
		);
	}

	getStartAndEndYearsFromPredictedPeriods() {
		const startPeriods = [];
		const endPeriods = [];

		this._predictedPeriods.forEach((monthPeriod) => {
			if (monthPeriod.getIndex() === 1) startPeriods.push(monthPeriod);
			if (monthPeriod.getIndex() === 12) endPeriods.push(monthPeriod);
		});

		if (this._isForecastingInCurrentYear()) {
			startPeriods.unshift(startPeriods[0].add(-12));
			const lastEndPeriod = endPeriods.at(-1);
			endPeriods.push(lastEndPeriod.add(12));
		}

		return startPeriods.map((startPeriod, index) =>
			this._makeYearFromPeriods({
				startPeriod,
				endPeriod: endPeriods[index],
			}),
		);
	}

	_isForecastingInCurrentYear() {
		return this._predictedPeriods[0].getIndex() !== 1;
	}

	_makeYearFromPeriods({ startPeriod, endPeriod }) {
		return {
			startYear: startPeriod.getYear(),
			startMonth: startPeriod.getCalendarIndex(),
			endYear: endPeriod.getYear(),
			endMonth: endPeriod.getCalendarIndex(),
		};
	}

	_findFiscalPeriodIndex({ fiscalYearPeriod }) {
		return this.getAllPeriods().findIndex((forecastPeriod) =>
			this._containsFiscalYearPeriod({
				fiscalYearPeriod,
				forecastPeriod,
			}),
		);
	}

	_containsFiscalYearPeriod({ fiscalYearPeriod, forecastPeriod }) {
		const fiscalYear = fiscalYearPeriod.getYear().getStart().getFullYear();

		return (
			forecastPeriod.getYear() === fiscalYear &&
			forecastPeriod.getIndex() === fiscalYearPeriod.getPeriodIndex()
		);
	}

	static transformMaterializationData(data, context) {
		super.transformMaterializationData(...arguments);

		data.creationDate = data.created && new Date(data.created);
		data.lastEditDate = data.lastUpdated && new Date(data.lastUpdated);

		data.referencePeriods &&= MonthPeriod.materializeAll(
			data.referencePeriods,
			context,
		);

		data.currentPeriod &&= MonthPeriod.materialize(
			data.currentPeriod,
			context,
		);

		data.predictedPeriods &&= MonthPeriod.materializeAll(
			data.predictedPeriods,
			context,
		);

		data.summaryValues &&= ForecastSummaryValue.materializeAll(
			data.summaryValues,
			context,
		);
	}
}
